/**
 * Third-party (gig courier) delivery types for the parts-delivery feature.
 *
 * These MIRROR the backend `parts_deliveries` row/quote shapes (sdk monorepo,
 * services/parts/functions/lib/api/parts-delivery/types.ts + the table columns)
 * so the mock client can be swapped for the real Hasura-backed client without
 * touching components. Do not invent fields here that the backend doesn't have.
 */

export type TpVendor = 'uber' | 'doordash';

export const VENDOR_LABELS: Record<TpVendor, string> = {
  uber: 'Uber Direct',
  doordash: 'DoorDash Drive',
};

/** parts_deliveries.delivery_status — the unified vendor lifecycle. */
export type TpDeliveryStatus =
  | 'pending'
  | 'driver_assigned'
  | 'arriving_pickup'
  | 'picked_up'
  | 'arriving_dropoff'
  | 'delivered'
  | 'canceled'
  | 'return_in_progress'
  | 'returned';

/** parts_deliveries.orchestration_status — the booking state machine. */
export type TpOrchestrationStatus = 'NEW' | 'PENDING' | 'BOOKING' | 'BOOKED' | 'RETRY' | 'FAILED';

/** One endpoint of the delivery (flattened address — UI-side shape). */
export interface TpPoint {
  name: string;
  business_name?: string;
  phone?: string;
  address: string;
  lat?: number | null;
  lng?: number | null;
  /** Courier-facing instructions at this waypoint (dropoff: the orders' special_instructions). */
  notes?: string;
  /** Package lookup code the courier matches at pickup — the order number(s).
   * DoorDash shows it to the Dasher as pickup_reference_tag. */
  reference_tag?: string;
}

export interface TpItem {
  name: string;
  quantity: number;
}

export interface TpQuoteRequest {
  pickup: TpPoint;
  dropoff: TpPoint;
  items: TpItem[];
  manifest_total_value_cents?: number;
  /** RFC 3339 — "Schedule later" pickup window start. Omit for ASAP. */
  ready_at?: string;
}

export interface TpQuote {
  vendor: TpVendor;
  quote_id: string;
  fee_cents: number;
  currency: string;
  pickup_eta?: string;
  dropoff_eta?: string;
  /** Quotes are short-lived (~5 min) — drives the countdown on the card. */
  expires_at?: string;
}

export interface TpQuoteResult {
  quotes: TpQuote[];
  /** Vendors that failed to quote (e.g. Uber before credentials exist). */
  errors: { vendor: TpVendor; message: string }[];
}

export interface TpBookRequest extends TpQuoteRequest {
  requested_vendor: TpVendor;
  quote_id?: string;
  /** The shell mobility_runs id this delivery is attached to. */
  run_id: number;
  customer_id: number;
  created_by: string;
  /** Dropoff verification the courier must perform (backend maps to the
   * vendor's dropoff options — DoorDash dropoff_options / signature). The
   * portal defaults every booking to requires_photo. */
  verification?: { requires_photo?: boolean; requires_signature?: boolean };
}

/** parts_deliveries.driver jsonb — the vendor's courier feed, normalized. */
export interface TpDriver {
  name?: string;
  phone?: string;
  vehicle?: { make?: string; model?: string; year?: string; license_plate?: string };
  lat?: number;
  lng?: number;
}

/** parts_deliveries.proof_of_delivery jsonb. */
export interface TpProof {
  signature_url?: string;
  photo_urls?: string[];
}

/** The live delivery state the UI renders — a projection of the parts_deliveries row. */
export interface TpDeliveryState {
  parts_delivery_id: string;
  orchestration_status: TpOrchestrationStatus;
  delivery_status: TpDeliveryStatus;
  vendor: TpVendor;
  fee_cents?: number;
  currency?: string;
  driver?: TpDriver;
  dropoff_eta?: string;
  tracking_url?: string;
  proof_of_delivery?: TpProof;
  booked_at?: string;
  completed_at?: string;
  cancelation_reason?: string;
  last_error?: { message?: string } | null;
  created_by?: string;
}
